import { useState, useEffect } from "react";
import { API_BASE } from "../../utils/api";
import { useIsMobile } from "../../hooks/useIsMobile";

interface Props {
  listingId: string;
  brokerName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function InquiryModal({ listingId, brokerName, onClose, onSuccess }: Props) {
  const m = useIsMobile();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // 로그인 시 자동 채움
  useEffect(() => {
    try {
      const info = JSON.parse(localStorage.getItem("broker_info") || "null");
      if (info) {
        setName(info.name || "");
        setPhone(info.phone || "");
      }
    } catch { /* guest */ }
  }, []);

  const handleSubmit = async () => {
    if (name.length < 2) { setError("이름은 2자 이상 입력해주세요"); return; }
    if (!/^\d{10,11}$/.test(phone.replace(/-/g, ""))) { setError("올바른 연락처를 입력해주세요"); return; }
    if (message.length < 10) { setError("문의 내용은 10자 이상 입력해주세요"); return; }

    setSending(true);
    setError("");

    try {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      const token = localStorage.getItem("broker_token");
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/inquiries`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          listing_id: listingId,
          sender_name: name,
          sender_phone: phone.replace(/-/g, ""),
          message,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "전송 실패" }));
        throw new Error((err as any).error || "전송 실패");
      }

      onSuccess();
    } catch (e: any) {
      setError(e.message || "전송에 실패했습니다");
    } finally {
      setSending(false);
    }
  };

  const st = styles(m);

  return (
    <div style={st.overlay} onClick={onClose}>
      <div style={st.modal} onClick={(e) => e.stopPropagation()}>
        <div style={st.header}>
          <h3 style={st.title}>{"\uD83D\uDCE8"} {brokerName} {"\uC911\uAC1C\uC0AC\uB2D8\uC5D0\uAC8C \uBB38\uC758"}</h3>
          <button style={st.closeBtn} onClick={onClose}>{"\u00D7"}</button>
        </div>

        <div style={st.form}>
          <div style={st.field}>
            <label style={st.label}>{"\uC774\uB984"}</label>
            <input
              style={st.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={"\uC774\uB984\uC744 \uC785\uB825\uD574\uC8FC\uC138\uC694"}
            />
          </div>

          <div style={st.field}>
            <label style={st.label}>{"\uC5F0\uB77D\uCC98"}</label>
            <input
              style={st.input}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="01012345678"
              type="tel"
            />
          </div>

          <div style={st.field}>
            <label style={st.label}>{"\uBB38\uC758 \uB0B4\uC6A9"}</label>
            <textarea
              style={st.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={"\uBB38\uC758\uD558\uC2E4 \uB0B4\uC6A9\uC744 \uC791\uC131\uD574\uC8FC\uC138\uC694 (10\uC790 \uC774\uC0C1)"}
              rows={4}
            />
          </div>

          {error && <div style={st.error}>{error}</div>}

          <button style={st.submitBtn} onClick={handleSubmit} disabled={sending}>
            {sending ? "\uC804\uC1A1 \uC911..." : "\uBB38\uC758 \uBCF4\uB0B4\uAE30"}
          </button>

          {/* 직접 문의 */}
          <div style={st.directContact}>
            <span style={st.directLabel}>{"직접 문의"}</span>
            <a href="tel:07082258585" style={st.contactLink}>{"\uD83D\uDCDE"} 070-8225-8585</a>
            <span style={st.contactSep}>|</span>
            <a href="mailto:admin@genomic.cc" style={st.contactLink}>{"\u2709\uFE0F"} admin@genomic.cc</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const gold = "#D4AF37";

function styles(m: boolean): Record<string, React.CSSProperties> {
  return {
    overlay: {
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center",
      padding: m ? 16 : 20,
    },
    modal: {
      background: "var(--bg-card)", border: "1px solid var(--border-light)",
      borderRadius: 16, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto" as const,
    },
    header: {
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: m ? "16px 16px 0" : "20px 24px 0",
    },
    title: {
      fontSize: m ? 16 : 17, fontWeight: 700, color: "var(--text-primary)", margin: 0,
    },
    closeBtn: {
      background: "none", border: "none", color: "var(--text-muted)",
      fontSize: 24, cursor: "pointer", padding: 0, lineHeight: 1,
    },
    form: {
      padding: m ? 16 : 24, display: "flex", flexDirection: "column" as const, gap: 14,
    },
    field: {
      display: "flex", flexDirection: "column" as const, gap: 6,
    },
    label: {
      fontSize: 13, fontWeight: 600, color: "var(--text-secondary)",
    },
    input: {
      padding: "10px 12px", borderRadius: 8, border: "1px solid var(--border-medium)",
      background: "var(--bg-secondary)", color: "var(--text-primary)",
      fontSize: 16, outline: "none",
    },
    textarea: {
      padding: "10px 12px", borderRadius: 8, border: "1px solid var(--border-medium)",
      background: "var(--bg-secondary)", color: "var(--text-primary)",
      fontSize: 16, outline: "none", resize: "vertical" as const, minHeight: 100,
      fontFamily: "inherit",
    },
    error: {
      fontSize: 13, color: "#EF4444", padding: "8px 12px",
      background: "rgba(239,68,68,0.1)", borderRadius: 8,
    },
    submitBtn: {
      padding: "13px 0", borderRadius: 10, border: "none",
      background: `linear-gradient(135deg, #B8972E, ${gold})`,
      color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer",
      marginTop: 4,
    },
    directContact: {
      display: "flex", alignItems: "center", justifyContent: "center",
      flexWrap: "wrap" as const, gap: 4,
      padding: "10px 0 0",
      borderTop: "1px solid var(--border-light)",
      marginTop: 4,
    },
    directLabel: {
      fontSize: 11, color: "var(--text-muted)", marginRight: 4,
    },
    contactLink: {
      fontSize: 12, color: "var(--text-muted)",
      textDecoration: "none",
    },
    contactSep: {
      fontSize: 11, color: "var(--border-medium)", margin: "0 4px",
    },
  };
}
